import React, { useState } from "react";
import classnames from "classnames";

import ArrowRightGrey from "@vahak/core/dist/icons/ArrowRightGrey.svg";
import LocationPinOutlined from "@vahak/core/dist/standard-icons/location-pin-outlined.svg";

import { DetailedRouteModal, DetailedRouteLoadDetail } from "../detailed-route";
import useHomepageV2Translation from "../hooks/useHomepageV2Translation";
import { getRouteLocationDisplay, LoadRouteLocationFields } from "./getRouteLocationDisplay";
import Styles from "./LoadMarketRouteSection.module.scss";

interface RouteLocationBlockProps {
    label: string;
    city?: string;
    state?: string;
    align?: "left" | "right";
    idPrefix?: string;
    side?: "source" | "destination";
}

const RouteLocationBlock = ({ label, city, state, align = "left", idPrefix, side }: RouteLocationBlockProps) => (
    <div
        className={classnames(Styles.locationBlock, {
            [Styles.locationBlockRight]: align === "right"
        })}
    >
        <span className={Styles.locationLabel}>{label}</span>
        <span
            className={Styles.locationCity}
            id={idPrefix && side ? `${idPrefix}--${side === "source" ? "source" : "destination"}--city` : undefined}
        >
            {city ? `${city},` : "--"}
        </span>
        {state ? (
            <span
                className={Styles.locationState}
                id={
                    idPrefix && side ? `${idPrefix}--${side === "source" ? "source" : "destination"}--state` : undefined
                }
            >
                {state}
            </span>
        ) : null}
    </div>
);

export interface LoadMarketRouteSectionProps {
    loadDetail?: LoadRouteLocationFields &
        DetailedRouteLoadDetail & {
            distance?: number;
            stops?: unknown[];
            id?: number;
        };
    variant?: "marketCard" | "bidDetails";
    idPrefix?: string;
    onStopsClick?: () => void;
    onShowRouteClick?: () => void;
}

const LoadMarketRouteSection = ({
    loadDetail,
    variant = "marketCard",
    idPrefix,
    onStopsClick,
    onShowRouteClick
}: LoadMarketRouteSectionProps) => {
    const { t } = useHomepageV2Translation();
    const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
    const { sourceCity, sourceState, destinationCity, destinationState } = getRouteLocationDisplay(loadDetail);
    const stopsCount = loadDetail?.stops?.length || 0;
    const canOpenRoute = Boolean(loadDetail?.id);
    const showRouteLink = variant === "bidDetails" && canOpenRoute;

    const openRouteModal = () => {
        if (canOpenRoute) {
            setIsRouteModalOpen(true);
        }
    };

    const handleStopsClick = () => {
        onStopsClick?.();
        openRouteModal();
    };

    const handleShowRouteClick = () => {
        onShowRouteClick?.();
        openRouteModal();
    };

    return (
        <>
            <div
                className={classnames(Styles.routeSection, {
                    [Styles.routeSectionMarketCard]: variant === "marketCard",
                    [Styles.routeSectionBidDetails]: variant === "bidDetails"
                })}
            >
                <RouteLocationBlock
                    label={t("mktCardV2:from")}
                    city={sourceCity}
                    state={sourceState}
                    idPrefix={idPrefix}
                    side="source"
                />

                <div
                    className={classnames(Styles.routeCenter, {
                        [Styles.routeCenterBidDetails]: variant === "bidDetails"
                    })}
                >
                    {stopsCount > 0 ? (
                        <button
                            type="button"
                            className={classnames(Styles.routeActionBtn, {
                                [Styles.routeActionBtnBidDetails]: variant === "bidDetails"
                            })}
                            onClick={handleStopsClick}
                            id={idPrefix ? `${idPrefix}--stops` : undefined}
                        >
                            +{stopsCount} {stopsCount === 1 ? t("mktCardV2:stop") : t("mktCardV2:stops")}
                        </button>
                    ) : (
                        <span
                            className={classnames(Styles.stopsSpacer, {
                                [Styles.stopsSpacerBidDetails]: variant === "bidDetails"
                            })}
                            aria-hidden
                        />
                    )}
                    <div className={Styles.arrowIcon}>
                        <ArrowRightGrey />
                    </div>
                    {loadDetail?.distance ? (
                        <span
                            className={classnames(Styles.distanceBadge, {
                                [Styles.distanceBadgeBidDetails]: variant === "bidDetails"
                            })}
                            id={idPrefix ? `${idPrefix}--distance` : undefined}
                        >
                            {Math.round(loadDetail.distance / 1000)} {t("mktCardV2:kms")}
                        </span>
                    ) : (
                        <span
                            className={classnames(Styles.stopsSpacer, {
                                [Styles.distanceSpacerBidDetails]: variant === "bidDetails"
                            })}
                            aria-hidden
                        />
                    )}
                </div>

                <RouteLocationBlock
                    label={t("mktCardV2:to")}
                    city={destinationCity}
                    state={destinationState}
                    align="right"
                    idPrefix={idPrefix}
                    side="destination"
                />

                {showRouteLink ? (
                    <button type="button" className={Styles.showRouteBtn} onClick={handleShowRouteClick}>
                        <LocationPinOutlined className={Styles.showRouteIcon} aria-hidden />
                        {t("mktCardV2:showRouteOnMap")}
                    </button>
                ) : null}
            </div>

            {canOpenRoute ? (
                <DetailedRouteModal
                    isOpen={isRouteModalOpen}
                    onClose={() => setIsRouteModalOpen(false)}
                    loadId={loadDetail!.id!}
                    loadDetail={loadDetail}
                />
            ) : null}
        </>
    );
};

export default LoadMarketRouteSection;
